import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useStore } from '../../../../store/store';
import { HEADER_MESSAGE_TYPES } from '../../../../utils/constants/header';
import GeneralLoader from '../../../../components/loaders/GeneralLoader';

const TEMPLATE_URL = '/bulk-company-template.xlsx';
const ACCEPT = '.xlsx';
// 5 MB ceiling is a UX guard only (even a 10k-row name-only xlsx is well under 1 MB); the backend
// must still enforce its own row/size limit. Default pending Hamid's product call (see openQuestions).
const MAX_BYTES = 5 * 1024 * 1024;

// Returns an error message, or '' when the file is valid.
const validateFile = (file) => {
  if (!file) {
    return 'Please choose a file.';
  }
  if (!/\.xlsx$/i.test(file.name)) {
    return 'Please upload an .xlsx file created from the template.';
  }
  if (file.size === 0) {
    return 'This file looks empty. Please upload the completed template.';
  }
  if (file.size > MAX_BYTES) {
    return 'This file is too large (max 5 MB). Please upload the completed template.';
  }
  return '';
};

/**
 * Shared copy for the demo-account gate. Lives here so every render site of the
 * dropzone reaches for the same string instead of re-typing it.
 */
export const BULK_DEMO_UPLOAD_HINT =
  "Bulk upload isn't available on a demo account. Upgrade to full access to upload a company list.";

/**
 * Interactive upload dropzone for the Bulk company search.
 * Handles file picking (click + drag/drop), .xlsx validation, the selected-file chip,
 * and the "Download template" link. The parent owns the selected file via onFileChange.
 *
 * TD-2382: validation errors surface through the standard top-of-screen system message
 * (utilsStore.setHeaderMessage) rather than as inline text under the box; while the parent
 * runs the upload/poll the box itself flips to an in-field loading animation (`loading`),
 * keeping the surrounding form layout intact.
 *
 * TD-2480: that in-field loading state also carries the live "Matching companies… X / N"
 * counter (fed by useBulkCompanySearch's `progress`), so the whole match runs inside the
 * upload frame instead of a separate loader elsewhere on the page.
 */
const BulkFileDropzone = ({
  file,
  onFileChange,
  loading = false,
  progress = null,
  disabled = false,
  disabledHint = ''
}) => {
  const { utilsStore } = useStore();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const openPicker = () => {
    if (loading || disabled) {
      return;
    }
    inputRef.current?.click();
  };

  const handleFiles = (files) => {
    const picked = files?.[0];
    if (!picked) {
      return;
    }
    const validationError = validateFile(picked);
    if (validationError) {
      utilsStore?.setHeaderMessage?.(validationError, HEADER_MESSAGE_TYPES.ERROR);
      onFileChange(null);
      return;
    }
    onFileChange(picked);
  };

  const clearFile = () => {
    onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const interactive = !loading && !disabled;
  let stateClass = 'enabled';
  if (disabled) {
    stateClass = 'disabled';
  } else if (loading) {
    stateClass = 'loading';
  }
  const containerClass = [
    'download-container',
    stateClass,
    dragOver && interactive ? 'dragover' : '',
    file && interactive ? 'has-file' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const renderBody = () => {
    if (loading) {
      const total = progress?.total;
      const processed = progress?.processed ?? 0;
      const label = total ? `Matching companies… ${processed} / ${total}` : 'Matching companies…';
      return (
        <div className="dropzone-loading" role="status" aria-live="polite">
          <GeneralLoader wrapClassName="bulk-loading-dots" />
          <div className="dropzone-loading-text">{label}</div>
        </div>
      );
    }
    if (file) {
      return (
        <div className="selected-file">
          <div className="file-name">{file.name}</div>
          <button type="button" className="remove-file" onClick={clearFile}>
            Remove
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        className="dropzone-trigger"
        onClick={openPicker}
        aria-disabled={disabled}
      >
        <div className="upload-icon"></div>
        <div className="upload-info-row">
          <div>Drag and drop a file or&nbsp;</div>
          <div className="choose-file-link">Choose from your device</div>
        </div>
      </button>
    );
  };

  return (
    <div className="profiler-download-wrap">
      <div className="download-label">
        Upload a file before executing the search<font className="red-asterisk">*</font>
      </div>
      <a className="template-download-link" href={TEMPLATE_URL} download>
        <span className="download-glyph" aria-hidden="true"></span>
        {'Download the Excel template'}
      </a>
      <div
        className={containerClass}
        onDragOver={(e) => {
          if (!interactive) {
            return;
          }
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          if (!interactive) {
            return;
          }
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        {renderBody()}
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          disabled={loading || disabled}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {disabled && disabledHint && <div className="dropzone-disabled-hint">{disabledHint}</div>}
    </div>
  );
};

BulkFileDropzone.propTypes = {
  file: PropTypes.object,
  onFileChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  progress: PropTypes.shape({
    processed: PropTypes.number,
    total: PropTypes.number
  }),
  disabled: PropTypes.bool,
  disabledHint: PropTypes.string
};

export default BulkFileDropzone;
